import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import SectionHeader from "../../../components/home/membershipPlanComponents/SectionHeader";
import WishlistItem from "../../../components/home/wishlistComponent/BookWishlistItem";
import EmptyWishlist from "../../../components/home/wishlistComponent/EmptyWishlist";

import {
  fetchWishlist,
  removeFromWishlistAsync,
} from "../../../features/wishlistSlice";
import { addToCartAsync } from "../../../features/cartSlice";

/**
 * Safely parses any date format (ISO string, array, or timestamp).
 * Returns a cleanly formatted date string (e.g. "07 Oct 2026") or null if invalid.
 */
export const formatWishlistDate = (dateInput) => {
  if (!dateInput) return null;
  try {
    let dateObj = null;
    if (typeof dateInput === "string" || typeof dateInput === "number") {
      dateObj = new Date(dateInput);
    } else if (Array.isArray(dateInput)) {
      const [year, month, day, hour = 0, minute = 0] = dateInput;
      if (!year || !month || !day) return null;
      dateObj = new Date(year, month - 1, day, hour, minute);
    }
    if (!dateObj || isNaN(dateObj.getTime())) {
      return null;
    }
    return dateObj.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return null;
  }
};

export default function Wishlist() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = useSelector((state) => state.auth.user?.userId);
  const { items = [], status, error } = useSelector((state) => state.wishlist);

  useEffect(() => {
    if (userId) {
      dispatch(fetchWishlist(userId));
    }
  }, [userId, dispatch]);

  const handleRemove = (bookId) => {
    dispatch(removeFromWishlistAsync({ userId, bookId }));
  };

  const handleAddToCart = (book) => {
    dispatch(addToCartAsync({ userId, book }));
  };

  const isMultiple = items.length > 1;

  return (
    <section className="w-full min-h-[60vh] pb-16">
      {/* Full-width section header */}
      <div className="w-full">
        <SectionHeader title="My Wishlist" />
      </div>

      {/* Main container: expands to max-w-6xl for 2-column grid when multiple books, max-w-4xl for single book */}
      <div
        className={`${
          isMultiple ? "max-w-6xl" : "max-w-4xl"
        } mx-auto px-4 sm:px-6 py-6 sm:py-8 transition-all duration-300`}
      >
        {/* Loading Skeleton */}
        {status === "loading" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="animate-pulse bg-white rounded-xl border border-gray-200 p-4 sm:p-5 flex flex-col justify-between gap-4"
              >
                <div className="flex gap-4 items-start">
                  <div className="w-20 h-28 sm:w-22 sm:h-30 bg-gray-200 rounded-lg flex-shrink-0" />
                  <div className="flex-1 space-y-2.5">
                    <div className="h-5 bg-gray-200 rounded w-3/4" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                    <div className="h-3 bg-gray-200 rounded w-1/3" />
                    <div className="h-5 bg-gray-200 rounded-full w-24 mt-2" />
                  </div>
                </div>
                <div className="flex gap-2.5 pt-3 border-t border-gray-100">
                  <div className="h-9 bg-gray-200 rounded-lg flex-1" />
                  <div className="h-9 bg-gray-200 rounded-lg w-24" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {status === "failed" && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-6 rounded-xl text-center space-y-3">
            <p className="font-medium text-base">
              {error || "Failed to load wishlist items"}
            </p>
            {userId && (
              <button
                onClick={() => dispatch(fetchWishlist(userId))}
                className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary-shade4 transition-colors"
              >
                Try Again
              </button>
            )}
          </div>
        )}

        {/* Empty State */}
        {status !== "loading" && status !== "failed" && items.length === 0 && (
          <EmptyWishlist />
        )}

        {/* Wishlist Items List */}
        {status !== "loading" && status !== "failed" && items.length > 0 && (
          <div className="space-y-4">
            {/* Top Summary Bar */}
            <div className="flex items-center justify-between pb-3 border-b border-gray-200/80 mb-2">
              <div className="flex items-center gap-2.5">
                <span className="font-semibold text-gray-800 text-base sm:text-lg">
                  Saved Books
                </span>
                <span className="bg-orange-100 text-primary text-xs font-bold px-2.5 py-0.5 rounded-full">
                  {items.length} {items.length === 1 ? "book" : "books"}
                </span>
              </div>

              <button
                onClick={() => navigate("/explore-books")}
                className="text-xs sm:text-sm font-medium text-primary hover:text-primary-shade4 transition-colors inline-flex items-center gap-1 group"
              >
                <span>Explore More</span>
                <span className="group-hover:translate-x-0.5 transition-transform">
                  &rarr;
                </span>
              </button>
            </div>

            {/* List: 2 books in a row (grid) if multiple books, single full-width card if 1 book */}
            <div
              className={
                isMultiple
                  ? "grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5"
                  : "space-y-4"
              }
            >
              {items.map((bookWrapper, index) => {
                const book = bookWrapper.book;
                if (!book) return null;

                const imageUrl =
                  bookWrapper.bookImages?.[0] || "/default-cover.jpg";
                const formattedDate = formatWishlistDate(
                  bookWrapper.creationDate
                );

                return (
                  <WishlistItem
                    key={book.bookId || index}
                    book={book}
                    imageUrl={imageUrl}
                    date={formattedDate}
                    onAddToCart={() => handleAddToCart(book)}
                    onRemove={() => handleRemove(book.bookId)}
                    isGrid={isMultiple}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
